import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Syringe, Scissors, Stethoscope, ClipboardList, ChevronLeft, ChevronRight, CalendarPlus, CheckCircle2, Clock } from 'lucide-react';

const EVENT_COLORS = {
  vaccination: { bg: 'bg-[#E9EFE8]', text: 'text-[#2D6A4F]', dot: 'bg-[#2D6A4F]' },
  farrier: { bg: 'bg-[#F9F7F3]', text: 'text-[#8B6914]', dot: 'bg-[#8B6914]' },
  vet_visit: { bg: 'bg-[#FEF3C7]', text: 'text-[#92400E]', dot: 'bg-[#92400E]' },
  chore: { bg: 'bg-[#E0E7FF]', text: 'text-[#3730A3]', dot: 'bg-[#3730A3]' },
};

const EVENT_ICONS = { vaccination: Syringe, farrier: Scissors, vet_visit: Stethoscope, chore: ClipboardList };

function buildGoogleCalendarUrl(event) {
  const date = event.date.replace(/-/g, '');
  const title = encodeURIComponent(event.title);
  const details = encodeURIComponent(`${event.type.replace('_', ' ')} for ${event.horse_name || 'your horse'} - via Stable Solutions`);
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${date}/${date}&details=${details}`;
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

export default function CalendarPage() {
  const { authHeaders, API } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const monthName = currentDate.toLocaleString('en-US', { month: 'long', year: 'numeric' });

  useEffect(() => {
    axios.get(`${API}/calendar/upcoming`, authHeaders())
      .then(res => setEvents(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const today = () => setCurrentDate(new Date());

  const getEventsForDate = (dateStr) => events.filter(e => e.date === dateStr);
  const todayStr = new Date().toISOString().slice(0, 10);

  const selectedEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  const handleMarkComplete = async (event) => {
    if (!event.record_id || !event.collection) return;
    try {
      await axios.post(`${API}/calendar/complete/${event.collection}/${event.record_id}`, {}, authHeaders());
      toast.success('Marked as complete!');
      fetchEvents();
    } catch (err) {
      toast.error('Failed to mark complete');
    }
  };

  // Build calendar grid
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="min-h-screen bg-[#F2F7F4] texture-grain">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-fade-in mb-6">
          <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#1B4332]" data-testid="calendar-title">
            Appointment Calendar
          </h1>
          <p className="text-[#4B6B58] mt-1">Upcoming vaccinations, farrier visits, vet follow-ups, and chores.</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Calendar Grid */}
          <div className="lg:col-span-2 bg-white rounded-[2rem] p-6 shadow-[0_8px_30px_rgb(27,67,50,0.06)] animate-fade-in-delay-1">
            <div className="flex items-center justify-between mb-6">
              <button onClick={prevMonth} data-testid="prev-month" className="p-2 rounded-full hover:bg-[#E9EFE8] text-[#4B6B58] btn-lift"><ChevronLeft size={20} /></button>
              <div className="text-center">
                <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332]" data-testid="current-month">{monthName}</h2>
                <button onClick={today} className="text-xs text-[#2D6A4F] font-medium hover:underline" data-testid="today-button">Today</button>
              </div>
              <button onClick={nextMonth} data-testid="next-month" className="p-2 rounded-full hover:bg-[#E9EFE8] text-[#4B6B58] btn-lift"><ChevronRight size={20} /></button>
            </div>

            {/* Weekday headers */}
            <div className="grid grid-cols-7 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <div key={d} className="text-center text-xs font-semibold text-[#4B6B58] py-2">{d}</div>
              ))}
            </div>

            {/* Days grid */}
            <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
              {cells.map((day, i) => {
                if (!day) return <div key={`empty-${i}`} />;
                const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const dayEvents = getEventsForDate(dateStr);
                const isToday = dateStr === todayStr;
                const isSelected = dateStr === selectedDate;

                return (
                  <button
                    key={dateStr}
                    onClick={() => setSelectedDate(dateStr)}
                    data-testid={`calendar-day-${dateStr}`}
                    className={`relative p-2 rounded-xl text-sm min-h-[52px] flex flex-col items-center btn-lift ${
                      isSelected ? 'bg-[#1B4332] text-white' : isToday ? 'bg-[#E9EFE8] text-[#1B4332] font-bold' : 'hover:bg-[#F2F7F4] text-[#122A1F]'
                    }`}
                  >
                    <span className="text-xs">{day}</span>
                    {dayEvents.length > 0 && (
                      <div className="flex gap-0.5 mt-1 flex-wrap justify-center">
                        {dayEvents.slice(0, 3).map((e, j) => (
                          <div key={j} className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white' : EVENT_COLORS[e.type]?.dot || 'bg-[#4B6B58]'}`} />
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-[#D1E0D5]">
              {Object.entries(EVENT_COLORS).map(([type, colors]) => {
                const Icon = EVENT_ICONS[type];
                return (
                  <div key={type} className="flex items-center gap-1.5 text-xs text-[#4B6B58]">
                    <div className={`w-2.5 h-2.5 rounded-full ${colors.dot}`} />
                    <span className="capitalize">{type.replace('_', ' ')}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Event Detail Panel */}
          <div className="bg-white rounded-[2rem] p-6 shadow-[0_8px_30px_rgb(27,67,50,0.06)] animate-fade-in-delay-2" data-testid="event-panel">
            <h3 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#1B4332] mb-4">
              {selectedDate
                ? new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
                : 'Select a date'
              }
            </h3>
            {!selectedDate ? (
              <p className="text-sm text-[#4B6B58]">Click a date to see scheduled events.</p>
            ) : selectedEvents.length === 0 ? (
              <p className="text-sm text-[#4B6B58]">No events scheduled for this date.</p>
            ) : (
              <div className="space-y-3">
                {selectedEvents.map((event, i) => {
                  const colors = EVENT_COLORS[event.type] || EVENT_COLORS.chore;
                  const Icon = EVENT_ICONS[event.type] || ClipboardList;
                  return (
                    <div key={i} className={`${colors.bg} rounded-xl p-4`} data-testid={`event-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Icon size={14} className={colors.text} />
                          <span className={`text-xs font-semibold uppercase tracking-wider ${colors.text}`}>
                            {event.type.replace('_', ' ')}
                          </span>
                        </div>
                        <a
                          href={buildGoogleCalendarUrl(event)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`gcal-${i}`}
                          className="flex items-center gap-1 text-[10px] font-medium text-[#2D6A4F] hover:underline bg-white/60 px-2 py-1 rounded-full"
                          title="Add to Google Calendar"
                        >
                          <CalendarPlus size={11} /> Google Cal
                        </a>
                      </div>
                      <p className="text-sm font-medium text-[#122A1F]">{event.title}</p>
                      {event.time && (
                        <p className="text-xs text-[#4B6B58] flex items-center gap-1 mt-1"><Clock size={10} /> {event.time}</p>
                      )}
                      <div className="flex items-center justify-between mt-1">
                        {event.horse_name && (
                          <Link to={`/horse/${event.horse_id}`} className="text-xs text-[#2D6A4F] hover:underline">
                            View {event.horse_name}'s records
                          </Link>
                        )}
                        {event.record_id && (
                          <button
                            onClick={() => handleMarkComplete(event)}
                            data-testid={`complete-event-${i}`}
                            className="flex items-center gap-1 text-[10px] font-medium text-green-600 hover:underline bg-green-50 px-2 py-1 rounded-full"
                          >
                            <CheckCircle2 size={11} /> Complete
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Upcoming events list */}
            <div className="mt-6 pt-4 border-t border-[#D1E0D5]">
              <h4 className="text-sm font-semibold text-[#1B4332] mb-3">Upcoming Events</h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {loading ? (
                  <p className="text-sm text-[#4B6B58] animate-pulse">Loading...</p>
                ) : events.filter(e => e.date >= todayStr).slice(0, 10).map((event, i) => {
                  const colors = EVENT_COLORS[event.type] || EVENT_COLORS.chore;
                  return (
                    <button
                      key={i}
                      onClick={() => {
                        const d = new Date(event.date + 'T12:00:00');
                        setCurrentDate(new Date(d.getFullYear(), d.getMonth(), 1));
                        setSelectedDate(event.date);
                      }}
                      className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-[#F2F7F4] text-left"
                    >
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${colors.dot}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-[#122A1F] truncate">{event.title}</p>
                        <p className="text-[10px] text-[#4B6B58]">{event.date}</p>
                      </div>
                    </button>
                  );
                })}
                {!loading && events.filter(e => e.date >= todayStr).length === 0 && (
                  <p className="text-xs text-[#4B6B58]">No upcoming events. Add records to see them here.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
